import { useEffect, useState } from 'react';

import { PredictiveDriveTimeReportRows } from '@/models/gen/graphql';
import React from 'react';
import Table from '@/common/Table';
import TimeIntervalFooter from '@/features/DriveTimeTool/components/TimeIntervalStats/TimeIntervalFooter';
import TimeIntervalHeader from '@/features/DriveTimeTool/components/TimeIntervalStats/TimeIntervalHeader';
import TimeIntervalRow from '@/features/DriveTimeTool/components/TimeIntervalStats/TimeIntervalRow';
import { createComponentState } from '@/state';
import { getPredictiveDriveTimeReport } from '@/features/DriveTimeTool/api/services/getPredictiveDriveTimeReport';
import { useDriveTimeTool } from '@/features/DriveTimeTool';

export type TimeIntervalStatsTableContext = {
  sorting: {
    column: string | undefined;
    direction: 'asc' | 'desc' | undefined;
  };
};
export const useTimeIntervalStatsTable = createComponentState<TimeIntervalStatsTableContext>({
  sorting: {
    column: 'timeFrame',
    direction: 'asc',
  },
});

const TimeIntervalStatsTable = (): JSX.Element => {
  const [{ selected, selectedFrom, timeFrameId, loading }, setState] = useDriveTimeTool(({ state, setState }) => [
    {
      selected: state.selected,
      selectedFrom: state.selectedFrom || 'queryHistoryId',
      timeFrameId: state.timeFrameId,
      loading: state.loading.intervals,
    },
    setState,
  ]);
  const [{ sortBy, sortDirection }] = useTimeIntervalStatsTable(({ state }) => [
    {
      sortBy: state.sorting.column,
      sortDirection: state.sorting.direction,
    },
  ]);
  const [stats, setStats] = useState<PredictiveDriveTimeReportRows[]>([]);

  const fetchData = async (id: string): Promise<void> => {
    if (loading) return;
    try {
      setState((state) => ({ ...state, loading: { ...state.loading, intervals: true } }));
      const res = await getPredictiveDriveTimeReport({
        [selectedFrom]: selected,
        timeFrameId: timeFrameId?.split('||')[0],
      });
      setStats(res?.values || []);
    } catch (e) {
      console.error('fetchData', e);
      setStats([]);
    } finally {
      setState((state) => ({ ...state, loading: { ...state.loading, intervals: false } }));
    }
  };

  useEffect(() => {
    if (!selected) return setStats([]);
    fetchData(selected);
  }, [selected, timeFrameId]);

  if (!stats.length)
    return (
      <div className="time-interval-content">
        <div className="ready-state">
          <div className="grid-icon-large">
            <i className="fas fa-th" />
          </div>
          <h3 className="ready-title">Ready for Time Interval Analysis</h3>
          <p className="ready-description">
            Select a query from the history above to view your exact uploaded data organized by time frames.
          </p>
        </div>
      </div>
    );

  const sortedStats = [...stats].sort((a, b) => {
    if (!sortBy || !sortDirection) return 0;
    const valA = a[sortBy === 'period' ? 'timeFrame' : sortBy];
    const valB = b[sortBy === 'period' ? 'timeFrame' : sortBy];
    if (typeof valA === 'number' && typeof valB === 'number') {
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    }
    if (typeof valA === 'string' && typeof valB === 'string') {
      return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    }
    return 0;
  });

  return (
    <Table header={<TimeIntervalHeader />} footer={<TimeIntervalFooter loading={loading} />}>
      {sortedStats.map((stat, index) => (
        <TimeIntervalRow key={index} data={stat} />
      ))}
    </Table>
  );
};

export default React.memo(TimeIntervalStatsTable);
